import {
    Body1,
    Body2,
    Box,
    Flex,
    HorizontalDivider,
    IconChevronRight,
    OutlinedCard,
    Stack,
    Subtitle1,
    Subtitle2,
    TextButton,
} from '@phx/design-system';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
    type MailOrderProviderInfoFragment,
    type PharmacyProviderInfoFragment,
    type PriorAuthorizationInfoFragment,
} from '../../../graphql/generated/graphql';
import { usePriorAuthActivities } from '../../../hooks/use-prior-authorization-activities';
import { formatToLocalizedDate, getAbsoluteRoute } from '../../../util';

export type PriorAuthLastActivityCardProps = {
    priorAuthorization: PriorAuthorizationInfoFragment;
    prescriberOrderId?: string;
    pharmacyProvider?:
        | PharmacyProviderInfoFragment
        | MailOrderProviderInfoFragment;
};

export const PriorAuthLastActivityCard = ({
    priorAuthorization,
    prescriberOrderId,
    pharmacyProvider,
}: PriorAuthLastActivityCardProps) => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();

    const navigateToPriorAuthStatus = useCallback(() => {
        navigate(
            getAbsoluteRoute('priorAuthorization.priorAuthorizationId.status', {
                priorAuthorizationId: priorAuthorization.id,
            })
        );
    }, [navigate, priorAuthorization]);

    const navigateToOrderConfirmation = useCallback(() => {
        if (prescriberOrderId && pharmacyProvider) {
            navigate(
                getAbsoluteRoute('orders.prescriberOrderId.confirmation', {
                    prescriberOrderId,
                })
            );
        }
    }, [navigate, prescriberOrderId, pharmacyProvider]);

    const activities = usePriorAuthActivities(
        priorAuthorization,
        navigateToPriorAuthStatus,
        navigateToOrderConfirmation,
        pharmacyProvider
    );

    return (
        <OutlinedCard>
            <Stack gap="xs">
                <Subtitle1>{t('orderDetails.lastActivity')}</Subtitle1>
                <HorizontalDivider />
                {activities.map((activity, idx) => {
                    const {
                        key,
                        status,
                        date,
                        description,
                        showChevron,
                        textButtonLabel,
                        onClick,
                    } = activity;

                    return (
                        <Stack key={key} gap="xxs">
                            <Flex justify="space-between">
                                <Subtitle2>{status}</Subtitle2>
                                <Body2>
                                    {formatToLocalizedDate(
                                        date,
                                        { dateStyle: 'medium' },
                                        i18n.language
                                    )}
                                </Body2>
                            </Flex>
                            {description ? <Body1>{description}</Body1> : null}
                            {textButtonLabel ? (
                                <Box>
                                    <TextButton
                                        RightIcon={
                                            showChevron
                                                ? IconChevronRight
                                                : undefined
                                        }
                                        onClick={onClick}
                                    >
                                        {textButtonLabel}
                                    </TextButton>
                                </Box>
                            ) : null}
                            {idx !== activities.length - 1 && (
                                <HorizontalDivider />
                            )}
                        </Stack>
                    );
                })}
            </Stack>
        </OutlinedCard>
    );
};
